import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Building, KeyRound, Snowflake, Shield, Clock, FileText, ArrowRight } from 'lucide-react';
import PageLayout from '../PageLayout';
import Seo from '../Seo';
import CTABlock from '../CTABlock';
import { graph, businessSchema, breadcrumbSchema } from '../../seo/site';
import '../ServicePage.css';

const PropertyServicesPage = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <>
      <Seo
        path="/property-services"
        title="Property Services Havering | Maintenance, Management & AC Servicing | Fallow"
        description="Planned & reactive property maintenance, landlord property management, and air conditioning servicing across Havering & East London. CIS-registered, 4-hour emergency response. Call 0203 576 5962."
        jsonLd={graph([
          businessSchema(),
          breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Property Services', path: '/property-services' },
          ]),
        ])}
      />

      <PageLayout
        heroTitle="Property Services in Havering & East London"
        heroSubtitle="Maintenance, management and compliance for portfolios, now including air conditioning servicing."
        heroImage="/images/hero-construction.jpg"
      >
        {/* Two doors */}
        <section className="service-benefits">
          <div className="service-container">
            <motion.div
              className="benefits-grid-modern"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <motion.div
                className="benefit-card-modern"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                whileHover={{ y: -4 }}
              >
                <div className="benefit-icon-wrapper">
                  <Building size={32} />
                </div>
                <h3 className="benefit-title">Property Maintenance (B2B)</h3>
                <p className="benefit-description">
                  Planned and reactive maintenance for property managers, letting agents, care homes, housing associations and commercial owners. All building fabric trades in-house under a single monthly invoice, 4-hour emergency response in our core area, RAMS documentation for all works.
                </p>
                <p className="benefit-description" style={{ marginTop: '12px' }}>
                  <Link to="/maintenance" className="contact-link">
                    View maintenance services <ArrowRight size={14} style={{ verticalAlign: 'middle' }} />
                  </Link>
                </p>
              </motion.div>

              <motion.div
                className="benefit-card-modern"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
                whileHover={{ y: -4 }}
              >
                <div className="benefit-icon-wrapper">
                  <KeyRound size={32} />
                </div>
                <h3 className="benefit-title">Property Management (Landlords)</h3>
                <p className="benefit-description">
                  Full property management for landlords: tenant finding and vetting, rent collection, inspections, compliance management and maintenance coordination, backed by our own in-house trades, so repairs are fast and properly finished.
                </p>
                <p className="benefit-description" style={{ marginTop: '12px' }}>
                  <Link to="/property-management" className="contact-link">
                    View property management <ArrowRight size={14} style={{ verticalAlign: 'middle' }} />
                  </Link>
                </p>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* AC servicing cross-sell */}
        <section className="service-benefits">
          <div className="service-container">
            <motion.div
              className="section-header"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="section-subtitle">New</span>
              <h2 className="section-title">Air Conditioning Servicing &amp; Compliance for Portfolios</h2>
            </motion.div>

            <motion.div
              className="content-block"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <p>We already maintain your buildings, and now we can install and service the cooling in them too. Offices, care homes, gyms, salons and server rooms run their systems continuously and need servicing two or three times a year; manufacturer warranties are conditional on it. One agreement, one invoice, the same compliance paperwork you already get from us.</p>
            </motion.div>

            <motion.div
              className="benefits-grid-modern"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              {[
                { icon: Snowflake, title: 'Planned AC Servicing', desc: 'Per-unit, per-visit pricing across your portfolio. Filter cleans, refrigerant checks and performance reports, all scheduled around your occupants.' },
                { icon: FileText, title: 'F-Gas Records Maintained', desc: 'Refrigerant handling records kept for your compliance file, integrated with the RAMS documentation we already provide.' },
                { icon: Clock, title: '4-Hour Emergency Response', desc: 'Category 1 emergencies attended within 4 hours in our core service area, now covering cooling failures in critical rooms.' },
                { icon: Shield, title: 'Single Monthly Invoice', desc: 'Servicing, repairs and installations across the portfolio on one invoice with clear job references per property.' },
              ].map((item, index) => {
                const Icon = item.icon;
                return (
                  <motion.div
                    key={item.title}
                    className="benefit-card-modern"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.08 * index }}
                    whileHover={{ y: -4 }}
                  >
                    <div className="benefit-icon-wrapper">
                      <Icon size={28} />
                    </div>
                    <h3 className="benefit-title">{item.title}</h3>
                    <p className="benefit-description">{item.desc}</p>
                  </motion.div>
                );
              })}
            </motion.div>

            <motion.div
              className="quick-links"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <Link to="/maintenance" className="btn-secondary">Property Maintenance</Link>
              <Link to="/property-management" className="btn-secondary">Property Management</Link>
              <Link to="/air-conditioning" className="btn-secondary">Air Conditioning</Link>
              <Link to="/contact" className="btn-secondary">Discuss Your Portfolio</Link>
            </motion.div>
          </div>
        </section>

        <CTABlock />
      </PageLayout>
    </>
  );
};

export default PropertyServicesPage;
